import { useEffect, useRef } from 'react';
import { Empty, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { ago, dateTime, shortDateTime, type SupportMessage } from './api';

/** The request's messages, oldest first, as chat bubbles. */
/** `userName` = the user's label (Display Name, else @handle) — see lib/listenerName `userLabel`. */
export function Conversation({ messages, userName }: { messages: SupportMessage[]; userName: string }) {
  const scroller = useRef<HTMLDivElement>(null);
  const lastId = messages[messages.length - 1]?.id;

  // Keep the newest message in view when the request opens or a message arrives.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  return (
    <div
      ref={scroller}
      style={{
        // Leaves the reply box on screen at a 900px-tall desktop viewport.
        height: 'clamp(260px, calc(100vh - 470px), 620px)',
        overflowY: 'auto',
        background: '#0F0F12',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {messages.length === 0 ? (
        <Empty style={{ margin: 'auto' }} description="No messages yet" />
      ) : (
        messages.map((m) => <Bubble key={m.id} m={m} userName={userName} />)
      )}
    </div>
  );
}

function Bubble({ m, userName }: { m: SupportMessage; userName: string }) {
  if (m.sender === 'system') {
    return (
      <div style={{ alignSelf: 'center', maxWidth: '85%', textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-block',
            fontSize: 12,
            color: '#8B8B8B',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 12,
            padding: '4px 12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {m.body}
        </div>
        <div style={{ fontSize: 11, color: '#5F5F66', marginTop: 2 }}>
          Automatic message · <Time iso={m.createdAt} />
        </div>
      </div>
    );
  }

  const fromSupport = m.sender === 'admin';
  const internal = m.internal;
  const name = fromSupport ? (m.admin?.name ?? 'Support') : userName;

  return (
    <div
      style={{
        alignSelf: fromSupport ? 'flex-end' : 'flex-start',
        maxWidth: 'min(78%, 620px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: fromSupport ? 'flex-end' : 'flex-start',
      }}
    >
      <div style={{ fontSize: 11, color: '#8B8B8B', marginBottom: 3, padding: '0 4px' }}>
        <strong style={{ color: '#C9C9CF' }}>{name}</strong> · <Time iso={m.createdAt} />
      </div>
      <div
        style={{
          padding: '8px 12px',
          borderRadius: 12,
          borderTopLeftRadius: fromSupport ? 12 : 4,
          borderTopRightRadius: fromSupport ? 4 : 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: 14,
          lineHeight: 1.45,
          ...(internal
            ? { background: '#FFF1B8', color: '#3B2F00', border: '1px dashed #D4B106' }
            : fromSupport
              ? { background: 'rgba(251,60,127,0.16)', color: 'white', border: '1px solid rgba(251,60,127,0.35)' }
              : { background: '#1F1F24', color: 'white', border: '1px solid rgba(255,255,255,0.08)' }),
        }}
      >
        {internal && (
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7A5C00', marginBottom: 4 }}>
            <LockOutlined /> Internal note — not visible to the user
          </div>
        )}
        {m.body}
      </div>
    </div>
  );
}

function Time({ iso }: { iso: string }) {
  return (
    <Tooltip title={`${dateTime(iso)} (${ago(iso)})`}>
      <span>{shortDateTime(iso)}</span>
    </Tooltip>
  );
}
